import { EDisplays, selectDisplay, setDisplay } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';

export const testId = 'reviewNav';

/**
 * A screen picker for reviewing copy locally.
 *
 * Clicking through the journey only reaches the happy path, so the states
 * that most need reviewing - referred, declined, ineligible, servicing
 * failure - cannot be seen that way. This jumps straight to any screen.
 *
 * It renders only when REACT_APP_REVIEW_MODE is set, which happens in
 * .env.local and nowhere else, so it is absent from every deployed build.
 * It only dispatches a display change; no validation is bypassed and no
 * application state is faked.
 */
export function ReviewNav(): JSX.Element | null {
    const dispatch = useAppDispatch();
    const display = useAppSelector(selectDisplay);

    if (process.env.REACT_APP_REVIEW_MODE !== 'true') return null;

    const screens = Object.entries(EDisplays).sort(([a], [b]) =>
        a.localeCompare(b)
    );

    return (
        <div
            data-testid={testId}
            className='fixed bottom-0 left-0 right-0 z-[9999] bg-black/85 text-white px-2 py-1 flex items-center gap-2'
        >
            <span className='text-[10px] uppercase tracking-wide shrink-0'>
                Review
            </span>
            <select
                aria-label='Jump to screen'
                className='flex-auto bg-black text-white text-xs p-1 rounded border border-white/30'
                value={display ?? ''}
                onChange={(e): void => {
                    dispatch(setDisplay(e.target.value as EDisplays));
                }}
            >
                {screens.map(([name, value]) => (
                    <option key={name} value={value}>
                        {name}
                    </option>
                ))}
            </select>
        </div>
    );
}
